import moment from 'moment-timezone';
import { mapGetters } from 'vuex';
import { WindowsToIanaTimeZones } from '@/mixins/ecs-time-formatter/windowsTimeZones';

export default {
  name: 'EcsTimeFormatter',

  computed: {
    ...mapGetters('SETTINGS', ['currentUserSettings', 'applicationSettings']),

    ...mapGetters('REFERENCES', ['availableTimeZones']),

    calendarFormat() {
      let calendarFormat = {};
      const displayFormat = this.applicationSettings.timeFormat;

      if (displayFormat === 'hh:mm A') {
        calendarFormat = {
          sameDay: `[${this.$t('todayAt')}] h:mm A`,
          lastDay: `[${this.$t('yesterdayAt')}] h:mm A`,
          lastWeek: `[${this.$t('last')}] dddd [${this.$t('at')}] h:mm A`,
          sameElse: `ll h:mm A`
        };
        calendarFormat.sameElse = `ll h:mm A`;
      } else if (displayFormat === 'HH:mm') {
        calendarFormat = {
          sameDay: `[${this.$t('todayAt')}] HH:mm`,
          lastDay: `[${this.$t('yesterdayAt')}] HH:mm`,
          lastWeek: `[${this.$t('last')}] dddd [${this.$t('at')}] HH:mm`,
          sameElse: `ll HH:mm`
        };
      }

      return calendarFormat;
    }
  },

  methods: {
    getIanaTimeZone(windowsTimeZoneId) {
      return WindowsToIanaTimeZones[windowsTimeZoneId] || 'America/New_York';
    },

    getCurrentTimeZone() {
      const currentUserTimeZoneId = this.applicationSettings.timeZoneId;
      return this.availableTimeZones.find(tz => tz.value === currentUserTimeZoneId) || {};
    },

    getCurrentTime() {
      const timeZone = this.getCurrentTimeZone();
      return moment().tz(this.getIanaTimeZone(timeZone.id));
    },

    getCurrentTimeFormatted(displayFormat = this.applicationSettings.timeFormat) {
      return this.getCurrentTime().format(displayFormat);
    },

    // This function is used to fix the timezone offset issue in the date string
    fixTzOffset(date, timezone) {
      const moment1 = moment(date).tz(timezone);

      if (typeof date === 'string' && moment1.isDST()) {
        const offset = moment1.utcOffset();
        const hours = Math.abs(offset) / 60;
        const minutes = Math.abs(offset) % 60;
        const sign = offset < 0 ? '-' : '+';
        const strOffset = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        return date.replace(/[+-]\d\d:\d\d$/g, strOffset);
      }

      return moment1;
    },

    getDiffInWordsWithTimezone(startDate, timezone1, endDate, timezone2) {
      const date1 = this.fixTzOffset(startDate, timezone1);
      const date2 = this.fixTzOffset(endDate, timezone2);

      const moment1 = moment(date1).tz(timezone1);
      const moment2 = moment(date2).tz(timezone2);

      const diffInMs = moment1.diff(moment2);

      const duration = moment.duration(Math.abs(diffInMs));
      const days = Math.floor(duration.asDays());
      const hours = duration.hours();
      const minutes = duration.minutes();

      let result = '';
      if (days > 0) {
        result += days + ' days ';
      } else {
        if (hours > 0) {
          if (hours === 1) {
            result += hours + ' hour ';
          } else {
            result += hours + ' hours ';
          }
        }
        if (minutes > 0) {
          if (minutes === 1) {
            result += minutes + ' minute';
          } else {
            result += minutes + ' minutes';
          }
        }
      }

      if (result === '') {
        return diffInMs > 500 ? `in a few seconds` : `few seconds ago`;
      }

      return diffInMs > 0 ? `in ${result.trim()}` : `${result.trim()} ago`;
    },

    formattedDateTime(date, timeZoneId) {
      const displayFormat = this.applicationSettings.timeFormat;

      const timeZone = this.getCurrentTimeZone();
      return moment(date)
        .tz(this.getIanaTimeZone(timeZoneId || timeZone.id))
        .format('YYYY-MM-DD ' + displayFormat || 'HH:mm:ss');
    },

    fromNow(timestamp, customerTimeZoneName, useCalendar = false) {
      const date = timestamp || Date.now();
      const timeZone = this.getCurrentTimeZone();

      if (!timeZone) {
        return moment.parseZone(date).calendar(null, this.calendarFormat);
      }

      if (useCalendar) {
        return moment(date)
          .tz(this.getIanaTimeZone(timeZone.id))
          .calendar(null, this.calendarFormat);
      }

      return this.getDiffInWordsWithTimezone(
        date,
        this.getIanaTimeZone(customerTimeZoneName),
        this.getCurrentTime(),
        this.getIanaTimeZone(timeZone.id)
      );
    }
  }
};
